import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { MdDelete } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import moment from "moment";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { LoadingPanel } from "../../components/layout/Loading";
import AlertBox from "../../components/common/AlertBox";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { clearBookingID, setBookingID } from "./bookingSlice";
import { deleteBooking, getAllBookings } from "./services/booking.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import BookingFilter from "./BookingFilter";

const Booking: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteBookingDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Booking List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={() => navigate("/booking/create")}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Booking
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <BookingGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const BookingGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const BookingList = useAppSelector((state) => state.booking.BookingList);
  const loading = useAppSelector((state) => state.booking.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterBookingDetails = useAppSelector(
    (state) => state.booking.FilterBookingDetails,
  );

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    const payload = {
      ...FilterBookingDetails,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllBookings(payload));
  }, [currentPage, pageLimit, FilterBookingDetails, dispatch]);

  const truncateText = (text: string) => {
    if (!text) return "";
    const maxLength = 80;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteBooking"));
      dispatch(setBookingID(ID));
    };

    return (
      <td
        {...props?.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props?.tdProps?.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() =>
              navigate("/booking/edit", {
                state: { BookingID: props.dataItem.ID },
              })
            }
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 8 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div style={{ position: "relative", marginBottom: "10px" }}>
        <BookingFilter />
      </div>

      <div
        style={{
          marginTop: 10,
          padding: "10px",
          border: "1px solid lightgray",
          borderRadius: "5px",
          boxShadow: "0 0 10px gainsboro",
        }}
      >
        {BookingList && BookingList.length ? (
          <KendoGrid
            style={{ maxHeight: "calc(100vh - 225px)" }}
            className="responsive-table"
            data={BookingList}
            total={BookingList[0]?.ItemCount || BookingList.length}
            skip={currentPage * pageLimit}
            take={pageLimit}
            pageable={{
              buttonCount: 5,
              pageSizes: [10, 50, 100, 300, 500],
              pageSizeValue: pageLimit,
              type: "input",
            }}
            onPageChange={pageChange}
            cells={{
              headerCell: HeaderCustomCell,
              data: MyDataCustomCell,
            }}
          >
            <Column
              field="Name"
              title="Customer Info"
              width={220}
              cell={(props: any) => (
                <td>
                  {isEditAccess ? (
                    <div
                      className="hoverableName fw-bold"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate("/booking/edit", {
                          state: { BookingID: props.dataItem?.ID },
                        })
                      }
                    >
                      {props.dataItem?.Name}
                    </div>
                  ) : (
                    <div className="fw-bold">{props.dataItem?.Name}</div>
                  )}
                  <p className="m-0 text-muted">{props.dataItem?.MobileNo}</p>
                  <p className="m-0 text-muted">{props.dataItem?.EmailID}</p>
                </td>
              )}
            />

            <Column
              field="BookingDate"
              title="Booking Date"
              width={140}
              cell={(props: any) => (
                <td>
                  {props.dataItem?.BookingDate
                    ? moment(props.dataItem.BookingDate).format("DD/MM/YYYY")
                    : "-"}
                </td>
              )}
            />

            <Column
              field="CheckinDate"
              title="Check-in Date & Time"
              width={200}
              cell={(props: any) => (
                <td>
                  <div>
                    {props.dataItem.CheckinDate
                      ? moment(props.dataItem.CheckinDate, "YYYY-MM-DD").format(
                          "DD/MM/YYYY",
                        )
                      : "-"}{" "}
                    {props.dataItem?.CheckinTime && (
                      <span>
                        {moment(props.dataItem.CheckinTime, "HH:mm:ss").format(
                          "hh:mm A",
                        )}
                      </span>
                    )}
                  </div>
                </td>
              )}
            />

            <Column
              field="RoomType"
              title="Room Type"
              width={140}
              cell={(props: any) => <td>{props.dataItem?.RoomType || "-"}</td>}
            />

            <Column
              field="NoOfRooms"
              title="Rooms / Pax"
              width={130}
              cell={(props: any) => (
                <td>
                  Rooms: <strong>{props.dataItem?.NoOfRooms || 0}</strong>
                  <br />
                  Pax: <strong>{props.dataItem?.NoOfPax || 0}</strong>
                </td>
              )}
            />

            <Column
              field="Source"
              title="Source"
              width={120}
              cell={(props: any) => <td>{props.dataItem?.Source || "-"}</td>}
            />

            <Column
              field="City"
              title="Location"
              width={150}
              cell={(props: any) => (
                <td>
                  {props.dataItem?.City}
                  {props.dataItem?.State ? `, ${props.dataItem.State}` : ""}
                </td>
              )}
            />

            <Column
              field="Remarks"
              title="Remarks"
              width={200}
              cell={(props: any) => (
                <td>
                  <div>
                    {expandedRow === props.dataItem?.ID
                      ? props.dataItem?.Remarks
                      : truncateText(props.dataItem?.Remarks)}
                  </div>
                  {props.dataItem?.Remarks?.length > 80 && (
                    <span
                      className="readmore-btn"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setExpandedRow(
                          expandedRow === props.dataItem?.ID
                            ? null
                            : props.dataItem?.ID,
                        )
                      }
                    >
                      <u>
                        {expandedRow === props.dataItem?.ID
                          ? "Read Less"
                          : "Read More"}
                      </u>
                    </span>
                  )}
                </td>
              )}
            />

            {(isEditAccess || isDeleteAccess) && (
              <Column
                field="ID"
                title="Actions"
                cells={{ data: MyEditCommandCell }}
                width={"110px"}
                filterable={false}
                locked={true}
              />
            )}
          </KendoGrid>
        ) : (
          <AlertBox />
        )}
      </div>
    </>
  );
};

const DeleteBookingDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.booking.loading);
  const BookingID = useAppSelector((state) => state.booking.BookingID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const FilterBookingDetails = useAppSelector(
    (state) => state.booking.FilterBookingDetails,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteBooking(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(closeDialog());
        dispatch(clearBookingID());
        dispatch(getAllBookings(FilterBookingDetails));
      }
    }
  };

  return (
    <>
      {dialogName === "deleteBooking" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Booking"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Booking?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(BookingID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Booking;
